/**
 * UI icon paths. Icons are vendored from the UE `assets/icons/` tree
 * (tools/vendor_icons.py) into `public/assets/icons/<category>/`, named by
 * data id: `buildings/aerarium.svg`, `resources/food.svg`,
 * `tech/tech_aerarium_revenue.svg`, `units/aquilifer.svg`.
 *
 * Availability is resolved against the build-time `index.json` (written by
 * the vendor script) so a missing icon degrades to the text label WITHOUT
 * a 404: iconImg emits no <img> when the icon is not indexed. Before
 * initIcons() resolves (or when the index is absent), iconImg emits the
 * <img> with an onerror self-hide as the safe fallback.
 */
export const ICON_BASE = "assets/icons";

export function buildingIcon(id: string): string {
  return `${ICON_BASE}/buildings/${id}.svg`;
}
export function resourceIcon(id: string): string {
  return `${ICON_BASE}/resources/${id}.svg`;
}
export function unitIcon(id: string): string {
  return `${ICON_BASE}/units/${id}.svg`;
}
export function techIcon(id: string): string {
  return `${ICON_BASE}/tech/${id}.svg`;
}

type IconCategory = "buildings" | "resources" | "tech" | "units";

/** Vendored icon ids per category, from public/assets/icons/index.json. */
const indexed = new Map<IconCategory, Set<string>>();
let initialized = false;

/** Load the icon index; call (and await) once during boot. Never throws. */
export async function initIcons(baseUrl = ""): Promise<void> {
  try {
    const res = await fetch(`${baseUrl}${ICON_BASE}/index.json`);
    if (!res.ok) return;
    const doc = (await res.json()) as Record<string, unknown>;
    for (const cat of ["buildings", "resources", "tech", "units"] as const) {
      const ids = doc[cat];
      if (Array.isArray(ids)) {
        indexed.set(
          cat,
          new Set(ids.filter((x): x is string => typeof x === "string")),
        );
      }
    }
  } catch {
    // No index (icons not vendored): iconImg falls back to onerror hiding.
  } finally {
    initialized = true;
  }
}

function categoryOf(src: string): IconCategory | null {
  const m = src.match(/^assets\/icons\/(buildings|resources|tech|units)\/([^/]+)\.svg$/);
  if (!m) return null;
  return m[1] as IconCategory;
}

/** True when the icon is indexed as vendored (false before initIcons). */
export function iconAvailable(src: string): boolean {
  const cat = categoryOf(src);
  if (!cat) return false;
  const id = src.slice(src.lastIndexOf("/") + 1, -".svg".length);
  return indexed.get(cat)?.has(id) ?? false;
}

/** Inline <img> for an icon path; empty string when the icon isn't vendored
 *  (after init), or a self-hiding <img> before the index has loaded. */
export function iconImg(src: string, alt: string, size = 18): string {
  if (initialized && !iconAvailable(src)) return "";
  return (
    `<img src="${src}" alt="${alt}" width="${size}" height="${size}" ` +
    `style="vertical-align:-4px;margin-right:6px;" onerror="this.style.display='none'"/>`
  );
}
